import React, { ReactNode, useEffect } from 'react';
import { PageView } from '../../../models/analytics';
import { ContentBody } from '../../atoms/ContentBody/ContentBody';
import { Button } from '../../atoms/Button/Button';
import { Header } from '../../organisms/Header/Header';
import { Footer } from '../../atoms/Footer/Footer';
import { Alert, AlertProps } from '../../molecules/Alert/Alert';
import { ContentOverlay } from '../../molecules/ContentOverlay/ContentOverlay';
import {
  ParentMessenger,
  parentMessenger as parentMessengerImport,
} from '../../../core/Analytics/ParentApplicationEventMessenger';
import { useAnalytics } from '../../../core/Analytics/AnalyticsProvider';
import { ErrorScreenDecoration, ErrorScreenStack } from './VerboseErrorScreen.styles';

export type VerboseErrorScreenProps = AlertProps & {
  viewEvent?: PageView;
  primaryAction?: () => void;
  primaryButtonText?: ReactNode;
  secondaryAction?: () => void;
  secondaryButtonText?: ReactNode;
  messenger?: ParentMessenger;
};

export const VerboseErrorScreen: React.FC<VerboseErrorScreenProps> = ({
  severity,
  title,
  body,
  error,
  primaryAction,
  primaryButtonText,
  secondaryAction,
  secondaryButtonText,
  viewEvent = PageView.ERROR_SCREEN,
  messenger = parentMessengerImport,
}) => {
  const { errorReason, errorStatus } = error || {};
  const { tinklinkAnalytics } = useAnalytics();

  const ifShowFooter =
    (primaryAction && primaryButtonText) || (secondaryAction && secondaryButtonText);

  useEffect(() => {
    if (errorReason) {
      tinklinkAnalytics.update({ errorReason: `${errorStatus}:${errorReason}` });
    }
    tinklinkAnalytics.sendViewEvent({ view: viewEvent });
    messenger.fireViewEvent(viewEvent);
  }, [errorReason, errorStatus, messenger, tinklinkAnalytics, viewEvent]);

  return (
    <ContentOverlay>
      <Header onBack={undefined} hideCloseButton pageView={viewEvent} />
      <ContentBody data-testid="verbose-error-screen" relative>
        <ErrorScreenStack justifyContent="center" role="alert">
          <Alert title={title} body={body} severity={severity} error={error} />
        </ErrorScreenStack>
        <ErrorScreenDecoration />
        {ifShowFooter && (
          <Footer>
            {primaryAction && primaryButtonText && (
              <Button
                color="primary"
                variant="contained"
                data-testid="verbose-error-primary-action"
                onClick={primaryAction}
              >
                {primaryButtonText}
              </Button>
            )}
            {secondaryAction && secondaryButtonText && (
              <Button
                color="primary"
                data-testid="verbose-error-secondary-action"
                onClick={secondaryAction}
              >
                {secondaryButtonText}
              </Button>
            )}
          </Footer>
        )}
      </ContentBody>
    </ContentOverlay>
  );
};
